import { Popover } from 'module/ui/components/atoms/popover';
import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';

const TOC_VISIBLE_LINES = 20;

const ToCContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  align-items: start;
  position: relative;
  z-index: 1;
`;

const ToCLine = styled.div<{ isActive: boolean; level: number }>`
  width: ${(props) =>
    props.level === 1 ? '24px' : props.level === 2 ? '16px' : '12px'};
  height: 2px;
  background-color: ${(props) =>
    props.isActive ? 'var(--button-primary)' : 'var(--gray-30)'};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  &:hover {
    opacity: 1;
    background-color: var(--button-primary);
  }
`;

const PopoverContent = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  min-width: 200px;
  max-width: 300px;
  padding: var(--spacing-04);
`;

const PopoverItem = styled.div<{ level: number; isActive: boolean }>`
  padding: var(--spacing-01) var(--spacing-04) var(--spacing-01)
    ${(props) => 8 + (props.level - 1) * 12}px;
  cursor: pointer;
  color: ${(props) =>
    props.isActive ? 'var(--link-color)' : 'var(--text-tertiary)'};

  transition: all 0.2s ease;
  font-size: var(--font-size-sm);

  &:hover {
    background-color: var(--gray-10);
    color: var(--text-primary);
    border-radius: var(--border-radius-xsmall);
  }
`;

type TOCItem = {
  id: string;
  textContent: string;
  level: number;
  isActive: boolean;
  isScrolledOver: boolean;
  itemIndex: number;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
interface ToCLineIndicatorProps {
  items: TOCItem[];
  editor: any;
}

export const ToCLineIndicator: React.FC<ToCLineIndicatorProps> = ({
  items,
  editor,
}) => {
  const isScrollingRef = useRef<boolean>(false);
  const [scrolledItemId, setScrolledItemId] = useState<string | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Function to find which item should be highlighted based on scroll position
  const findScrolledItem = useCallback((): string | null => {
    if (!editor) {
      return null;
    }

    const container = document.querySelector('.document-content-wrapper');

    if (!container) {
      return null;
    }

    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;

    let lastPassedItem: TOCItem | null = null;

    // Only consider the first 20 items for highlighting
    const itemsToCheck = items.slice(0, TOC_VISIBLE_LINES);

    itemsToCheck.forEach((item) => {
      const element = editor.view.dom.querySelector(
        `[data-toc-id="${item.id}"]`,
      );

      if (element) {
        const elementRect = element.getBoundingClientRect();
        const elementTop = elementRect.top;

        if (elementTop < containerTop + 130) {
          lastPassedItem = item;
        }
      }
    });

    const itemsBeyond20 = items.slice(TOC_VISIBLE_LINES);

    for (const item of itemsBeyond20) {
      const element = editor.view.dom.querySelector(
        `[data-toc-id="${item.id}"]`,
      );

      if (element) {
        const elementRect = element.getBoundingClientRect();
        const elementTop = elementRect.top;

        if (elementTop < containerTop + 130) {
          return null;
        }
      }
    }

    return lastPassedItem ? (lastPassedItem as TOCItem).id : null;
  }, [editor, items]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const container = document.querySelector('.document-content-wrapper');

    if (!container) {
      return;
    }

    const handleScroll = () => {
      if (isScrollingRef.current) {
        return;
      }

      const scrolledId = findScrolledItem();

      if (scrolledId !== scrolledItemId) {
        setScrolledItemId(scrolledId);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [editor, findScrolledItem, scrolledItemId]);

  const scrollToItem = useCallback(
    (id: string) => {
      if (editor) {
        const element = editor.view.dom.querySelector(`[data-toc-id="${id}"]`);

        if (element) {
          isScrollingRef.current = true;
          setScrolledItemId(id);
          setIsPopoverOpen(false);

          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });

          setTimeout(() => {
            isScrollingRef.current = false;
          }, 1300);
        }
      }
    },
    [editor],
  );

  // Only show first 20 items as lines - memoized to prevent recalculation
  const visibleLineItems = useMemo(
    () => items.slice(0, TOC_VISIBLE_LINES),
    [items],
  );

  // Popover content with all items - memoized to prevent unnecessary re-renders
  const popoverContent = useMemo(
    () => (
      <PopoverContent>
        {items.map((item) => (
          <PopoverItem
            key={item.id}
            level={item.level}
            isActive={scrolledItemId === item.id}
            onClick={() => scrollToItem(item.id)}
          >
            {item.textContent}
          </PopoverItem>
        ))}
      </PopoverContent>
    ),
    [items, scrolledItemId, scrollToItem],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Popover
      content={popoverContent}
      trigger="hover"
      placement="right"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      overlayStyle={{ zIndex: 1000 }}
      mouseEnterDelay={0}
      mouseLeaveDelay={0.1}
      align={{ offset: [-70, 50] }}
      getPopupContainer={(triggerNode) => {
        const editorWrapper = triggerNode.closest('.document-editor-wrapper');

        return (editorWrapper as HTMLElement) || document.body;
      }}
    >
      <div className="table-of-contents">
        <ToCContainer>
          {visibleLineItems.map((item) => (
            <ToCLine
              key={item.id}
              level={item.level}
              isActive={scrolledItemId === item.id}
              onClick={() => scrollToItem(item.id)}
              title={item.textContent}
            />
          ))}
        </ToCContainer>
      </div>
    </Popover>
  );
};
